// Draws a red highlight over any form field whose value contains the
// unsupported-character placeholder ("[?]" from sanitizeForPdf). An AcroForm
// text field renders as a single appearance with one colour, so we can't tint
// just the placeholder glyph — the whole field is highlighted instead, which
// is an unmissable flag that a character couldn't be rendered.
//
// Usage in a fill.js (the geometry must be read BEFORE flatten, drawn AFTER):
//   const hl = collectUnsupportedHighlights(pdfDoc, pdfForm);
//   pdfForm.flatten();
//   drawUnsupportedHighlights(pdfDoc, hl);

import { PDFName, rgb } from 'pdf-lib';
import { UNSUPPORTED_PLACEHOLDER } from './sanitizeForPdf';

/**
 * Find every text field containing the placeholder and return red highlight
 * rectangles for each of its widgets. MUST be called before pdfForm.flatten(),
 * while the fields and their widget geometry still exist.
 * @returns {{ pageIndex: number, x: number, y: number, width: number, height: number }[]}
 */
export function collectUnsupportedHighlights(pdfDoc, pdfForm) {
  const pages = pdfDoc.getPages();
  const rects = [];
  for (const field of pdfForm.getFields()) {
    if (typeof field.getText !== 'function') continue; // text fields only
    let text;
    try {
      text = field.getText();
    } catch {
      continue;
    }
    if (!text || !text.includes(UNSUPPORTED_PLACEHOLDER)) continue;

    for (const widget of field.acroField.getWidgets()) {
      const pageRef = widget.dict.get(PDFName.of('P'));
      const pageIndex = pageRef ? pages.findIndex((p) => p.ref === pageRef) : -1;
      if (pageIndex < 0) continue;
      const { x, y, width, height } = widget.getRectangle();
      rects.push({ pageIndex, x, y, width, height });
    }
  }
  return rects;
}

/**
 * Draw the rectangles collected by collectUnsupportedHighlights(). Call AFTER
 * pdfForm.flatten() so the highlight sits on top of the baked-in field text.
 */
export function drawUnsupportedHighlights(pdfDoc, rects) {
  if (!rects || !rects.length) return;
  const pages = pdfDoc.getPages();
  for (const { pageIndex, x, y, width, height } of rects) {
    const page = pages[pageIndex];
    if (!page) continue;
    page.drawRectangle({
      x: x - 2,
      y: y - 2,
      width: width + 4,
      height: height + 4,
      color: rgb(1, 0, 0),
      opacity: 0.22,
      borderColor: rgb(0.85, 0, 0),
      borderWidth: 1,
      borderOpacity: 0.9,
    });
  }
}
